// The product routes (Plan 4b Tasks 2–4) as TanStack Query hooks. Customer routes wait for the session's customer and
// key on it, so switching customer never shows another customer's cache. Failures stay typed (`ApiFailure`).
import {
  AnmEntityListResponse,
  CarteiraHideResponse,
  CarteiraResponse,
  ConsultaProcessResponse,
  ConsultaResponse,
  HojeResponse,
  LegacyProcessResponse,
  ObligationStateResponse,
  OnboardingResponse,
  PrazosResponse,
  StatusResponse,
  ProcessoResponse,
  ManualPrazoCreated,
  ManualLicenceResponse,
  ReviewSuggestionResponse,
  TrackRequestResponse,
  SeiListResponse,
  SeiProcessResponse,
  SeiReviewResponse,
  type AnmEntityOption,
  type ManualPrazoBody,
  type ManualLicenceBody,
  type ReviewSuggestionBody,
  type TrackRequestBody,
  type SeiVerdict,
  type HumanState,
  type ObligationView,
} from '@req/contracts';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { api, type ApiFailure, type ApiResult } from './api.ts';
import { useSession } from './session.ts';

/** A typed API failure, thrown into TanStack Query so `error` is the failure itself. */
export class ApiFailureError extends Error {
  constructor(readonly failure: ApiFailure) {
    super(failure.error);
  }
}
const unwrap = <T>(r: ApiResult<T>): T => {
  if (!r.ok) throw new ApiFailureError(r);
  return r.data;
};
export const failureOf = (e: unknown): ApiFailure | null => (e instanceof ApiFailureError ? e.failure : null);

/** The global titular filter (root search param) as the API's `anmEntity` value. */
export function useTitular(): string | undefined {
  const { titular } = useSearch({ strict: false }) as { titular?: string };
  return titular || undefined;
}
const withEntity = (path: string, anmEntity?: string) => (anmEntity ? `${path}?anmEntity=${anmEntity}` : path);

export function useHoje(anmEntity?: string) {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  return useQuery({
    queryKey: ['hoje', customerId, anmEntity ?? 'all'],
    queryFn: async () => unwrap(await api.get(withEntity('/hoje', anmEntity), HojeResponse)),
    enabled: !!customerId,
  });
}

/** The Status page and the rail's dot: the same for every customer; refreshed every minute (the API caches as long). */
export function useStatus() {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  return useQuery({
    queryKey: ['status'],
    queryFn: async () => unwrap(await api.get('/status', StatusResponse)),
    enabled: !!customerId,
    refetchInterval: 60_000,
  });
}

export function usePrazos(anmEntity?: string) {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  return useQuery({
    queryKey: ['prazos', customerId, anmEntity ?? 'all'],
    queryFn: async () => unwrap(await api.get(withEntity('/prazos', anmEntity), PrazosResponse)),
    enabled: !!customerId,
  });
}

/** An alert link sent before Plan 6 Task 6b (`/processos/<number>`) → the process's public id, or a 404. */
export function useLegacyProcess(n: string | null) {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  return useQuery({
    queryKey: ['processo-legado', customerId, n],
    queryFn: async () => unwrap(await api.get(`/processos/legado/${n}`, LegacyProcessResponse)),
    enabled: s.status === 'signed_in' && !!n,
    retry: false,
  });
}

export function useProcesso(processId: string) {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  return useQuery({
    queryKey: ['processo', customerId, processId],
    queryFn: async () => unwrap(await api.get(`/processos/${processId}`, ProcessoResponse)),
    enabled: s.status === 'signed_in',
  });
}

/** The titular filter's options: the AnmEntities the active customer sees (`GET /app/anm-entities`). */
export function useAnmEntities(): AnmEntityOption[] {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  const list = useQuery({
    queryKey: ['anm-entities', customerId],
    queryFn: async () => unwrap(await api.get('/app/anm-entities', AnmEntityListResponse)).items,
    enabled: !!customerId,
    staleTime: 5 * 60_000,
  });
  return list.data ?? [];
}

// --- SEI (Plan 6 Task 7): the module's own views ---------------------------------------------------------------------------

export function useSeiList(anmEntity?: string) {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  return useQuery({
    queryKey: ['sei', customerId, anmEntity ?? 'all'],
    queryFn: async () => unwrap(await api.get(withEntity('/sei', anmEntity), SeiListResponse)),
    enabled: !!customerId,
  });
}

export function useSeiProcess(seiProcessId: string) {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  return useQuery({
    queryKey: ['sei-processo', customerId, seiProcessId],
    queryFn: async () => unwrap(await api.get(`/sei/${seiProcessId}`, SeiProcessResponse)),
    enabled: !!customerId,
    retry: false,
  });
}

/** "É do titular" / "Não é do titular" on a possible homonym (null undoes it). */
export function useSeiReview(seiProcessId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (verdict: SeiVerdict | null) => unwrap(await api.post(`/sei/${seiProcessId}/review`, { verdict }, SeiReviewResponse)),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['sei'] }), qc.invalidateQueries({ queryKey: ['sei-processo'] })]),
  });
}

/** A Prazo from a SEI document (manual pendência): shows up in Prazos, Hoje and the process at once. */
export function useCreateManualPrazo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (x: { seiDocumentId: string; body: ManualPrazoBody }) =>
      unwrap(await api.post(`/sei/documentos/${x.seiDocumentId}/prazos`, x.body, ManualPrazoCreated)),
    onSuccess: () => Promise.all(['sei-processo', 'prazos', 'hoje', 'processo'].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
}

// --- Ambiental on a process (Plan 6 Task 8): follow a request by number, register a licence by hand ----------------

const refreshProcess = (qc: QueryClient) => Promise.all(['processo', 'prazos', 'hoje', 'ambiental-carteira'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
const Removed = z.object({ removed: z.boolean() });

export function useTrackEnvRequest(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TrackRequestBody) => unwrap(await api.post(`/processos/${processId}/ambiental/pedidos`, body, TrackRequestResponse)),
    onSuccess: () => refreshProcess(qc),
  });
}

export function useUntrackEnvRequest(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (watchId: string) => unwrap(await api.del(`/processos/${processId}/ambiental/pedidos/${watchId}`, Removed)),
    onSuccess: () => refreshProcess(qc),
  });
}

export function useAddManualLicence(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ManualLicenceBody) => unwrap(await api.post(`/processos/${processId}/ambiental/licencas`, body, ManualLicenceResponse)),
    onSuccess: () => refreshProcess(qc),
  });
}

export function useReviewSuggestion(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ReviewSuggestionBody) => unwrap(await api.post(`/processos/${processId}/ambiental/sugestoes`, body, ReviewSuggestionResponse)),
    onSuccess: () => refreshProcess(qc),
  });
}

export function useRemoveManualLicence(processId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (licenceId: string) => unwrap(await api.del(`/processos/${processId}/ambiental/licencas/${licenceId}`, Removed)),
    onSuccess: () => refreshProcess(qc),
  });
}

/** Processos → Ambientais, across processes: stop following a typed request, or remove a hand-registered licence, on
 *  the process it was registered on (the same routes as the process page). */
export function useRemoveOwnAmbiental() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (x: { processId: string; kind: 'pedidos' | 'licencas'; id: string }) =>
      unwrap(await api.del(`/processos/${x.processId}/ambiental/${x.kind}/${x.id}`, Removed)),
    onSuccess: () => refreshProcess(qc),
  });
}

// --- Onboarding and Carteira (Task 10) ---------------------------------------------------------------------------------

/** "Digite seu nome ou CNPJ": the holders a query resolves to (needs a session; its own rate-limit counters). */
export function useOnboarding(q: string | undefined) {
  const s = useSession();
  return useQuery({
    queryKey: ['onboarding', q],
    queryFn: async () => unwrap(await api.get(`/onboarding/resolve?q=${encodeURIComponent(q!)}`, OnboardingResponse)),
    enabled: s.status === 'signed_in' && !!q && q.trim().length >= 2,
    retry: false,
  });
}

/** The consultant's Carteira; `hidden` also lists the ones it hid. */
export function useCarteira(hidden: boolean) {
  const s = useSession();
  const customerId = s.status === 'signed_in' ? s.customerId : null;
  return useQuery({
    queryKey: ['carteira', customerId, hidden],
    queryFn: async () => unwrap(await api.get(`/app/carteira${hidden ? '?hidden=true' : ''}`, CarteiraResponse)),
    enabled: !!customerId,
  });
}

export function useCarteiraHide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (x: { subscriptionId: string; hidden: boolean }) =>
      unwrap(await api.post(`/app/carteira/${x.subscriptionId}/hide`, { hidden: x.hidden }, CarteiraHideResponse)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['carteira'] }),
  });
}

// --- Consulta (anonymous allowed) ---------------------------------------------------------------------------------------

export function useConsulta(q: string | undefined) {
  return useQuery({
    queryKey: ['consulta', q],
    queryFn: async () => unwrap(await api.get(`/consulta?q=${encodeURIComponent(q!)}`, ConsultaResponse)),
    enabled: !!q && q.trim().length >= 2,
    retry: false,
  });
}

export function useConsultaPessoa(personId: string | undefined) {
  return useQuery({
    queryKey: ['consulta-pessoa', personId],
    queryFn: async () => unwrap(await api.get(`/consulta/pessoas/${personId}`, ConsultaResponse)),
    enabled: !!personId,
    retry: false,
  });
}

export function useConsultaProcesso(processId: string | undefined) {
  return useQuery({
    queryKey: ['consulta-processo', processId],
    queryFn: async () => unwrap(await api.get(`/consulta/processos/${processId}`, ConsultaProcessResponse)),
    enabled: !!processId,
    retry: false,
  });
}

// --- acknowledge / resolve ----------------------------------------------------------------------------------------------

/** `manual`: a manual Prazo (Plan 6 Task 7) — same answers, its own routes. */
type Action = { obligationId: string; manual?: boolean } & ({ kind: 'acknowledge' } | { kind: 'resolve'; reason: string });

type NextState = (prev: HumanState | null) => HumanState;
const patchState = (o: ObligationView, id: string, next: NextState): ObligationView => (o.id === id ? { ...o, state: next(o.state) } : o);

/** Applies a new human state to every cached view of the obligation (Hoje, Prazos, Processo). */
function applyState(qc: QueryClient, id: string, state: NextState) {
  qc.setQueriesData<{ actNow: ObligationView[] }>({ queryKey: ['hoje'] }, (d) => (d ? { ...d, actNow: d.actNow.map((o) => patchState(o, id, state)) } : d));
  qc.setQueriesData<{ groups: { tier: string; obligations: ObligationView[] }[] }>({ queryKey: ['prazos'] }, (d) =>
    d ? { ...d, groups: d.groups.map((g) => ({ ...g, obligations: g.obligations.map((o) => patchState(o, id, state)) })) } : d,
  );
  qc.setQueriesData<{ layer: string; obligations?: ObligationView[] }>({ queryKey: ['processo'] }, (d) =>
    d && d.layer === 'full' && d.obligations ? { ...d, obligations: d.obligations.map((o) => patchState(o, id, state)) } : d,
  );
}

const EMPTY: HumanState = { acknowledgedBy: null, acknowledgedAt: null, resolvedBy: null, resolvedAt: null, resolvedReason: null };

/**
 * Ciente / Resolver (spec §7.6), optimistic: the row shows the new state at once and keeps its place (an acknowledged
 * obligation stays actionable); a failure puts the previous state back.
 */
export function useObligationAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: Action) => {
      const base = `${a.manual ? '/prazos-manuais' : '/obligations'}/${a.obligationId}`;
      return unwrap(
        a.kind === 'acknowledge'
          ? await api.post(`${base}/acknowledge`, {}, ObligationStateResponse)
          : await api.post(`${base}/resolve`, { reason: a.reason }, ObligationStateResponse),
      );
    },
    onMutate: async (a) => {
      await qc.cancelQueries({ queryKey: ['hoje'] });
      await qc.cancelQueries({ queryKey: ['prazos'] });
      const snapshot = qc.getQueriesData({ predicate: (q) => ['hoje', 'prazos', 'processo'].includes(String(q.queryKey[0])) });
      const now = new Date().toISOString();
      // the first "ciente" is kept (the API keeps who/when too); resolving acknowledges if nobody had
      applyState(qc, a.obligationId, (prev) => {
        const p = prev ?? EMPTY;
        const acked = p.acknowledgedAt ? { acknowledgedBy: p.acknowledgedBy, acknowledgedAt: p.acknowledgedAt } : { acknowledgedBy: 'você', acknowledgedAt: now };
        return { ...p, ...acked, ...(a.kind === 'resolve' ? { resolvedBy: 'você', resolvedAt: now, resolvedReason: a.reason } : {}) };
      });
      return { snapshot };
    },
    onError: (_e, _a, ctx) => {
      for (const [key, data] of ctx?.snapshot ?? []) qc.setQueryData(key, data);
    },
    onSuccess: (r) => applyState(qc, r.obligationId, () => r.state),
  });
}
